import {
  IconBlockquote,
  IconCaretRightFilled,
  IconChevronRight,
  IconCheckbox,
  IconCode,
  IconH1,
  IconH2,
  IconH3,
  IconInfoCircle,
  IconList,
  IconListNumbers,
  IconMath,
  IconMathFunction,
  IconMovie,
  IconMusic,
  IconPaperclip,
  IconFileTypePdf,
  IconPhoto,
  IconTable,
  IconTypography,
  IconMenu4,
  IconCalendar,
  IconAppWindow,
  IconSitemap,
  IconColumns3,
  IconColumns2,
  IconTag,
  IconLayoutNavbar,
  IconApi,
  IconLink,
  IconLayoutGrid,
} from "@tabler/icons-react";
import {
  CommandProps,
  SlashMenuGroupedItemsType,
} from "@/features/editor/components/slash-menu/types";
import { uploadImageAction } from "@/features/editor/components/image/upload-image-action.tsx";
import { uploadVideoAction } from "@/features/editor/components/video/upload-video-action.tsx";
import { uploadAudioAction } from "@/features/editor/components/audio/upload-audio-action.tsx";
import { uploadAttachmentAction } from "@/features/editor/components/attachment/upload-attachment-action.tsx";
import { uploadPdfAction } from "@/features/editor/components/pdf/upload-pdf-action.tsx";
import { pickAndUploadIcons } from "@/features/editor/components/layout-blocks/upload-icons.ts";
import { openConfigureChips } from "@/features/editor/components/layout-blocks/configure-chips-modal.tsx";
import { openConfigureFeatures } from "@/features/editor/components/layout-blocks/configure-features-modal.tsx";
import { openSingleIconText } from "@/features/editor/components/layout-blocks/single-icon-text-modal.tsx";
import { openTextPrompt } from "@/features/editor/components/layout-blocks/text-prompt-modal.tsx";
import { notifications } from "@mantine/notifications";
import IconExcalidraw from "@/components/icons/icon-excalidraw";
import IconMermaid from "@/components/icons/icon-mermaid";
import IconDrawio from "@/components/icons/icon-drawio";
import { IconColumns4 } from "@/components/icons/icon-columns-4";
import { IconColumns5 } from "@/components/icons/icon-columns-5";
import {
  AirtableIcon,
  FigmaIcon,
  FramerIcon,
  GoogleDriveIcon,
  GoogleSheetsIcon,
  LoomIcon,
  MiroIcon,
  TypeformIcon,
  VimeoIcon,
  YoutubeIcon,
} from "@/components/icons";

const CommandGroups: SlashMenuGroupedItemsType = {
  basic: [
    {
      title: "Text",
      description: "Just start typing with plain text.",
      searchTerms: ["p", "paragraph"],
      icon: IconTypography,
      command: ({ editor, range }: CommandProps) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .toggleNode("paragraph", "paragraph")
          .run();
      },
    },
    {
      title: "To-do list",
      description: "Track tasks with a to-do list.",
      searchTerms: ["todo", "task", "list", "check", "checkbox"],
      icon: IconCheckbox,
      command: ({ editor, range }: CommandProps) => {
        editor.chain().focus().deleteRange(range).toggleTaskList().run();
      },
    },
    {
      title: "Heading 1",
      description: "Big section heading.",
      searchTerms: ["title", "big", "large"],
      icon: IconH1,
      command: ({ editor, range }: CommandProps) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setNode("heading", { level: 1 })
          .run();
      },
    },
    {
      title: "Heading 2",
      description: "Medium section heading.",
      searchTerms: ["subtitle", "medium"],
      icon: IconH2,
      command: ({ editor, range }: CommandProps) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setNode("heading", { level: 2 })
          .run();
      },
    },
    {
      title: "Heading 3",
      description: "Small section heading.",
      searchTerms: ["subtitle", "small"],
      icon: IconH3,
      command: ({ editor, range }: CommandProps) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setNode("heading", { level: 3 })
          .run();
      },
    },
    {
      title: "Bullet list",
      description: "Create a simple bullet list.",
      searchTerms: ["unordered", "point", "list"],
      icon: IconList,
      command: ({ editor, range }: CommandProps) => {
        editor.chain().focus().deleteRange(range).toggleBulletList().run();
      },
    },
    {
      title: "Numbered list",
      description: "Create a list with numbering.",
      searchTerms: ["numbered", "ordered", "list"],
      icon: IconListNumbers,
      command: ({ editor, range }: CommandProps) => {
        editor.chain().focus().deleteRange(range).toggleOrderedList().run();
      },
    },
    {
      title: "Quote",
      description: "Create block quote.",
      searchTerms: ["blockquote", "quotes"],
      icon: IconBlockquote,
      command: ({ editor, range }: CommandProps) =>
        editor.chain().focus().deleteRange(range).toggleBlockquote().run(),
    },
    {
      title: "Code",
      description: "Insert code snippet.",
      searchTerms: ["codeblock"],
      icon: IconCode,
      command: ({ editor, range }: CommandProps) =>
        editor.chain().focus().deleteRange(range).toggleCodeBlock().run(),
    },
    {
      title: "Divider",
      description: "Insert horizontal rule divider",
      searchTerms: ["horizontal rule", "hr"],
      icon: IconMenu4,
      command: ({ editor, range }: CommandProps) =>
        editor.chain().focus().deleteRange(range).setHorizontalRule().run(),
    },
  ],
  media: [
    {
      title: "Image",
      description: "Upload any image from your device.",
      searchTerms: ["photo", "picture", "media", "file", "attachment"],
      icon: IconPhoto,
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).run();

        // @ts-ignore
        const pageId = editor.storage?.pageId;
        if (!pageId) return;

        // upload image
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        input.multiple = true;
        input.style.display = "none";
        document.body.appendChild(input);
        input.onchange = async () => {
          if (input.files?.length) {
            for (const file of input.files) {
              const pos = editor.view.state.selection.from;

              uploadImageAction(file, editor, pos, pageId);
            }
          }

          input.remove();
        };
        input.click();
      },
    },
    {
      title: "Video",
      description: "Upload any video from your device.",
      searchTerms: ["video", "mp4", "media", "file", "attachment"],
      icon: IconMovie,
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).run();

        // @ts-ignore
        const pageId = editor.storage?.pageId;
        if (!pageId) return;

        // upload video
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "video/*";
        input.multiple = true;
        input.style.display = "none";
        document.body.appendChild(input);
        input.onchange = async () => {
          if (input.files?.length) {
            for (const file of input.files) {
              const pos = editor.view.state.selection.from;

              uploadVideoAction(file, editor, pos, pageId);
            }
          }

          input.remove();
        };
        input.click();
      },
    },
    {
      title: "Audio",
      description: "Upload any audio from your device.",
      searchTerms: ["audio", "music", "sound", "mp3", "media", "file", "attachment"],
      icon: IconMusic,
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).run();

        // @ts-ignore
        const pageId = editor.storage?.pageId;
        if (!pageId) return;

        // upload audio
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "audio/*";
        input.multiple = true;
        input.style.display = "none";
        document.body.appendChild(input);
        input.onchange = async () => {
          if (input.files?.length) {
            for (const file of input.files) {
              const pos = editor.view.state.selection.from;

              uploadAudioAction(file, editor, pos, pageId);
            }
          }

          input.remove();
        };
        input.click();
      },
    },
    {
      title: "Embed PDF",
      description: "Upload and embed a PDF file.",
      searchTerms: ["pdf", "document", "embed"],
      icon: IconFileTypePdf,
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).run();

        // @ts-ignore
        const pageId = editor.storage?.pageId;
        if (!pageId) return;

        const input = document.createElement("input");
        input.type = "file";
        input.accept = "application/pdf";
        input.style.display = "none";
        document.body.appendChild(input);
        input.onchange = async () => {
          if (input.files?.length) {
            for (const file of input.files) {
              const pos = editor.view.state.selection.from;

              uploadPdfAction(file, editor, pos, pageId);
            }
          }

          input.remove();
        };
        input.click();
      },
    },
    {
      title: "File attachment",
      description: "Upload any file from your device.",
      searchTerms: ["file", "attachment", "upload", "csv", "zip"],
      icon: IconPaperclip,
      command: ({ editor, range }) => {
        editor.chain().focus().deleteRange(range).run();

        // @ts-ignore
        const pageId = editor.storage?.pageId;
        if (!pageId) return;

        // upload file
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "";
        input.multiple = true;
        input.style.display = "none";
        document.body.appendChild(input);
        input.onchange = async () => {
          if (input.files?.length) {
            for (const file of input.files) {
              const pos = editor.view.state.selection.from;

              uploadAttachmentAction(file, editor, pos, pageId, true);
            }
          }

          input.remove();
        };
        input.click();
      },
    },
  ],
  advanced: [
    {
      title: "Table",
      description: "Insert a table.",
      searchTerms: ["table", "rows", "columns"],
      icon: IconTable,
      command: ({ editor, range }: CommandProps) =>
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
          .run(),
    },
    {
      title: "Toggle block",
      description: "Insert collapsible block.",
      searchTerms: ["collapsible", "block", "toggle", "details", "expand"],
      icon: IconCaretRightFilled,
      command: ({ editor, range }: CommandProps) =>
        editor.chain().focus().deleteRange(range).setDetails().run(),
    },
    {
      title: "Expandable",
      description: "Insert a collapsible accordion block.",
      searchTerms: ["expandable", "details", "toggle", "collapse", "accordion"],
      icon: IconChevronRight,
      command: ({ editor, range }: CommandProps) =>
        editor.chain().focus().deleteRange(range).insertExpandable().run(),
    },
    {
      title: "Tabs",
      description: "Tabbed content blocks.",
      searchTerms: ["tabs", "tab", "tabbed", "switcher"],
      icon: IconLayoutNavbar,
      command: ({ editor, range }: CommandProps) =>
        editor.chain().focus().deleteRange(range).insertTabs().run(),
    },
    {
      title: "Cards",
      description: "Grid of cards with cover, title and description.",
      searchTerms: ["cards", "grid", "card grid", "gallery"],
      icon: IconLayoutGrid,
      command: ({ editor, range }: CommandProps) =>
        editor.chain().focus().deleteRange(range).insertCards({ columns: 3 }).run(),
    },
    {
      title: "Cards (2 cols)",
      description: "Grid of cards, 2 columns.",
      searchTerms: ["cards", "grid", "card grid", "2", "two"],
      icon: IconLayoutGrid,
      command: ({ editor, range }: CommandProps) =>
        editor.chain().focus().deleteRange(range).insertCards({ columns: 2 }).run(),
    },
    {
      title: "Cards (3 cols)",
      description: "Grid of cards, 3 columns.",
      searchTerms: ["cards", "grid", "card grid", "3", "three"],
      icon: IconLayoutGrid,
      command: ({ editor, range }: CommandProps) =>
        editor.chain().focus().deleteRange(range).insertCards({ columns: 3 }).run(),
    },
    {
      title: "Cards (4 cols)",
      description: "Grid of cards, 4 columns.",
      searchTerms: ["cards", "grid", "card grid", "4", "four"],
      icon: IconLayoutGrid,
      command: ({ editor, range }: CommandProps) =>
        editor.chain().focus().deleteRange(range).insertCards({ columns: 4 }).run(),
    },
    {
      title: "Stepper",
      description: "Step-by-step instructions with numbered circles.",
      searchTerms: ["stepper", "steps", "howto", "guide", "tutorial", "walkthrough"],
      icon: IconListNumbers,
      command: ({ editor, range }: CommandProps) =>
        editor.chain().focus().deleteRange(range).insertStepper(2).run(),
    },
    {
      title: "API reference",
      description: "HTTP method + endpoint URL with highlighted {params}.",
      searchTerms: ["api", "endpoint", "rest", "http", "get", "post", "reference"],
      icon: IconApi,
      command: ({ editor, range }: CommandProps) =>
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertApiReference({
            method: "get",
            url: "https://api.example.com/v1/{apiKey}/resource/{id}",
          })
          .run(),
    },
    {
      title: "API Reference Step",
      description: "Numbered API workflow step with method, path, and description.",
      searchTerms: ["api", "reference", "step", "workflow", "numbered", "v3", "rest", "http", "endpoint"],
      icon: IconApi,
      command: ({ editor, range }: CommandProps) =>
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertApiReferenceV3({
            step: 1,
            title: "Complete information",
            method: "get",
            path: "/reference/list",
            description:
              "Look up valid country IDs, periods, targets, and carriers. You'll need these IDs for the next two steps.",
          })
          .run(),
    },
    {
      title: "API Reference Card",
      description: "Endpoint card with method, path, title, description, and Use when.",
      searchTerms: ["api", "reference", "endpoint", "card", "v2", "rest", "http"],
      icon: IconApi,
      command: ({ editor, range }: CommandProps) =>
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertApiReferenceV2({
            method: "get",
            path: "/auth/{id}",
            title: "List authorizations",
            description: "Which login/password pairs or whitelisted IPs have access.",
            useWhen: "auditing all data tied to a client email.",
          })
          .run(),
    },
    {
      title: "Embed URL",
      description: "Insert a link as a rich card (title + favicon).",
      searchTerms: ["embed", "url", "link", "bookmark", "card"],
      icon: IconLink,
      command: ({ editor, range }: CommandProps) => {
        // Insert an empty embed — the NodeView auto-opens its Mantine
        // Modal when `url` is empty so the user can paste/type there.
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertEmbedUrl({ url: "" })
          .run();
      },
    },
    {
      title: "Callout",
      description: "Insert callout notice.",
      searchTerms: [
        "callout",
        "notice",
        "panel",
        "info",
        "warning",
        "success",
        "error",
        "danger",
      ],
      icon: IconInfoCircle,
      command: ({ editor, range }: CommandProps) =>
        editor.chain().focus().deleteRange(range).toggleCallout().run(),
    },
    {
      title: "Math inline",
      description: "Insert inline math equation.",
      searchTerms: [
        "math",
        "inline",
        "mathinline",
        "inlinemath",
        "inline math",
        "equation",
        "katex",
        "latex",
        "tex",
      ],
      icon: IconMathFunction,
      command: ({ editor, range }: CommandProps) =>
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setMathInline()
          .setNodeSelection(range.from)
          .run(),
    },
    {
      title: "Math block",
      description: "Insert math equation",
      searchTerms: [
        "math",
        "block",
        "mathblock",
        "block math",
        "equation",
        "katex",
        "latex",
        "tex",
      ],
      icon: IconMath,
      command: ({ editor, range }: CommandProps) =>
        editor.chain().focus().deleteRange(range).setMathBlock().run(),
    },
    {
      title: "Mermaid diagram",
      description: "Insert mermaid diagram",
      searchTerms: ["mermaid", "diagrams", "chart", "uml"],
      icon: IconMermaid,
      command: ({ editor, range }: CommandProps) =>
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setCodeBlock({ language: "mermaid" })
          .insertContent("flowchart LR\n" + "    A --> B")
          .run(),
    },
    {
      title: "Draw.io (diagrams.net)",
      description: "Insert and design Drawio diagrams",
      searchTerms: ["drawio", "diagrams", "charts", "uml", "whiteboard"],
      icon: IconDrawio,
      command: ({ editor, range }: CommandProps) =>
        editor.chain().focus().deleteRange(range).setDrawio().run(),
    },
    {
      title: "Excalidraw (Whiteboard)",
      description: "Draw and sketch excalidraw diagrams",
      searchTerms: ["diagrams", "draw", "sketch", "whiteboard"],
      icon: IconExcalidraw,
      command: ({ editor, range }: CommandProps) =>
        editor.chain().focus().deleteRange(range).setExcalidraw().run(),
    },
    {
      title: "Date",
      description: "Insert current date",
      searchTerms: ["date", "today"],
      icon: IconCalendar,
      command: ({ editor, range }: CommandProps) => {
        const currentDate = new Date().toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });

        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertContent(currentDate)
          .run();
      },
    },
    {
      title: "Status",
      description: "Insert inline status badge.",
      searchTerms: ["status", "badge", "label", "lozenge"],
      icon: IconTag,
      command: ({ editor, range }: CommandProps) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setStatus({ text: "", color: "gray" })
          .run();
      },
    },
    {
      title: "Subpages (Child pages)",
      description: "List all subpages of the current page",
      searchTerms: ["subpages", "child", "children", "nested", "hierarchy"],
      icon: IconSitemap,
      command: ({ editor, range }: CommandProps) => {
        editor.chain().focus().deleteRange(range).insertSubpages().run();
      },
    },
    {
      title: "2 Columns",
      description: "Split content into two columns.",
      searchTerms: ["columns", "layout", "split", "side"],
      icon: IconColumns2,
      command: ({ editor, range }: CommandProps) =>
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertColumns({ layout: "two_equal" })
          .run(),
    },
    {
      title: "3 Columns",
      description: "Split content into three columns.",
      searchTerms: ["columns", "layout", "split", "triple"],
      icon: IconColumns3,
      command: ({ editor, range }: CommandProps) =>
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertColumns({ layout: "three_equal" })
          .run(),
    },
    {
      title: "4 Columns",
      description: "Split content into four columns.",
      searchTerms: ["columns", "layout", "split"],
      icon: IconColumns4,
      command: ({ editor, range }: CommandProps) =>
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertColumns({ layout: "four_equal" })
          .run(),
    },
    {
      title: "5 Columns",
      description: "Split content into five columns.",
      searchTerms: ["columns", "layout", "split"],
      icon: IconColumns5,
      command: ({ editor, range }: CommandProps) =>
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertColumns({ layout: "five_equal" })
          .run(),
    },
    {
      title: "Iframe embed",
      description: "Embed any Iframe",
      searchTerms: ["iframe"],
      icon: IconAppWindow,
      command: ({ editor, range }: CommandProps) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setEmbed({ provider: "iframe" })
          .run();
      },
    },
    {
      title: "Airtable",
      description: "Embed Airtable",
      searchTerms: ["airtable"],
      icon: AirtableIcon,
      command: ({ editor, range }: CommandProps) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setEmbed({ provider: "airtable" })
          .run();
      },
    },
    {
      title: "Loom",
      description: "Embed Loom video",
      searchTerms: ["loom"],
      icon: LoomIcon,
      command: ({ editor, range }: CommandProps) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setEmbed({ provider: "loom" })
          .run();
      },
    },
    {
      title: "Figma",
      description: "Embed Figma files",
      searchTerms: ["figma"],
      icon: FigmaIcon,
      command: ({ editor, range }: CommandProps) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setEmbed({ provider: "figma" })
          .run();
      },
    },
    {
      title: "Typeform",
      description: "Embed Typeform",
      searchTerms: ["typeform"],
      icon: TypeformIcon,
      command: ({ editor, range }: CommandProps) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setEmbed({ provider: "typeform" })
          .run();
      },
    },
    {
      title: "Miro",
      description: "Embed Miro board",
      searchTerms: ["miro"],
      icon: MiroIcon,
      command: ({ editor, range }: CommandProps) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setEmbed({ provider: "miro" })
          .run();
      },
    },
    {
      title: "YouTube",
      description: "Embed YouTube video",
      searchTerms: ["youtube", "yt", "media", "video"],
      icon: YoutubeIcon,
      command: ({ editor, range }: CommandProps) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setEmbed({ provider: "youtube" })
          .run();
      },
    },
    {
      title: "Vimeo",
      description: "Embed Vimeo video",
      searchTerms: ["vimeo"],
      icon: VimeoIcon,
      command: ({ editor, range }: CommandProps) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setEmbed({ provider: "vimeo" })
          .run();
      },
    },
    {
      title: "Framer",
      description: "Embed Framer prototype",
      searchTerms: ["framer"],
      icon: FramerIcon,
      command: ({ editor, range }: CommandProps) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setEmbed({ provider: "framer" })
          .run();
      },
    },
    {
      title: "Google Drive",
      description: "Embed Google Drive content",
      searchTerms: ["google drive", "gdrive"],
      icon: GoogleDriveIcon,
      command: ({ editor, range }: CommandProps) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setEmbed({ provider: "gdrive" })
          .run();
      },
    },
    {
      title: "Google Sheets",
      description: "Embed Google Sheets content",
      searchTerms: ["google sheets", "gsheets"],
      icon: GoogleSheetsIcon,
      command: ({ editor, range }: CommandProps) => {
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .setEmbed({ provider: "gsheets" })
          .run();
      },
    },
  ],
  layout: [
    {
      title: "Inline image",
      description: "Upload a small image that flows inline with text (icon-sized). Use inside table cells or paragraphs.",
      searchTerms: ["inline", "image", "icon", "small", "image", "table", "cell"],
      icon: IconPhoto,
      command: async ({ editor, range }: CommandProps) => {
        editor.chain().focus().deleteRange(range).run();
        // @ts-ignore
        const pageId = editor.storage?.pageId as string | undefined;
        if (!pageId) {
          notifications.show({
            color: "red",
            message: "Cannot insert: page is not saved yet (no pageId).",
          });
          return;
        }
        try {
          const [ic] = await pickAndUploadIcons(pageId);
          if (!ic) return;
          // Use the file's natural width as-is. User chose the file, so
          // they pick the size implicitly. If they upload something huge
          // they'll see and resize with the handle.
          const display = ic.naturalWidth ?? 32;
          const ok = editor
            .chain()
            .focus()
            .insertInlineImage({
              src: ic.src,
              alt: ic.alt,
              width: display,
              naturalWidth: ic.naturalWidth,
              naturalHeight: ic.naturalHeight,
              attachmentId: ic.attachmentId,
            })
            .run();
          if (!ok) {
            notifications.show({
              color: "red",
              message: "Insert failed: schema rejected inlineImage.",
            });
          }
        } catch (err: any) {
          notifications.show({
            color: "red",
            message: `Inline image: ${err?.message ?? String(err)}`,
          });
        }
      },
    },
    {
      title: "Hero split",
      description: "Two-column hero: narrow left sidebar + wide content.",
      searchTerms: ["hero", "split", "two", "columns", "sidebar", "layout"],
      icon: IconColumns2,
      command: ({ editor, range }: CommandProps) =>
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertColumns({ layout: "two_left_sidebar" })
          .run(),
    },
    {
      title: "Two columns",
      description: "Two equal columns of rich content.",
      searchTerms: ["columns", "two", "2", "split", "layout"],
      icon: IconColumns2,
      command: ({ editor, range }: CommandProps) =>
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertColumns({ layout: "two_equal" })
          .run(),
    },
    {
      title: "Three columns",
      description: "Three equal columns of rich content.",
      searchTerms: ["columns", "three", "3", "split", "layout"],
      icon: IconColumns3,
      command: ({ editor, range }: CommandProps) =>
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertColumns({ layout: "three_equal" })
          .run(),
    },
    {
      title: "Four columns",
      description: "Four equal columns of rich content.",
      searchTerms: ["columns", "four", "4", "split", "layout"],
      icon: IconColumns4,
      command: ({ editor, range }: CommandProps) =>
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertColumns({ layout: "four_equal" })
          .run(),
    },
    {
      title: "Five columns",
      description: "Five equal columns of rich content.",
      searchTerms: ["columns", "five", "5", "split", "layout"],
      icon: IconColumns5,
      command: ({ editor, range }: CommandProps) =>
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertColumns({ layout: "five_equal" })
          .run(),
    },
    {
      title: "Mapping table (3 rows)",
      description: "Two-column associations with an arrow between cells.",
      searchTerms: ["mapping", "table", "pairs", "arrow", "3"],
      icon: IconTable,
      command: ({ editor, range }: CommandProps) =>
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertMappingTable({ rows: 3 })
          .run(),
    },
    {
      title: "Mapping table (5 rows)",
      description: "Two-column associations, 5 rows.",
      searchTerms: ["mapping", "table", "pairs", "arrow", "5"],
      icon: IconTable,
      command: ({ editor, range }: CommandProps) =>
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertMappingTable({ rows: 5 })
          .run(),
    },
    {
      title: "Mapping table (↔ double arrow, 3 rows)",
      description: "Two-column associations with a double-headed arrow.",
      searchTerms: ["mapping", "double", "arrow", "↔", "bidirectional"],
      icon: IconTable,
      command: ({ editor, range }: CommandProps) =>
        editor
          .chain()
          .focus()
          .deleteRange(range)
          .insertMappingTable({ rows: 3, connector: "double-arrow" })
          .run(),
    },
    {
      title: "Icon chip grid",
      description: "Pick 2–6 icons (uploaded to B2). Each becomes a chip with an editable label.",
      searchTerms: ["icon", "chip", "grid", "row", "tags"],
      icon: IconTag,
      command: async ({ editor, range }: CommandProps) => {
        // Drop the typed slash + query synchronously, BEFORE any async work.
        // If we leave deleteRange in the final chain, the stored `range`
        // goes stale during upload/modal and the `/` stays in the doc.
        editor.chain().focus().deleteRange(range).run();
        // @ts-ignore — pageId is stamped onto editor.storage by page-editor.
        const pageId = editor.storage?.pageId as string | undefined;
        if (!pageId) {
          notifications.show({
            color: "red",
            message: "Cannot insert: page is not saved yet (no pageId).",
          });
          return;
        }
        try {
          const icons = await pickAndUploadIcons(pageId, { multiple: true });
          if (icons.length === 0) return;
          const initial = icons.slice(0, 6).map((ic) => ({
            iconSrc: ic.src,
            iconAlt: ic.alt,
            label: ic.alt.replace(/\.[^.]+$/, ""),
            href: "",
            variant: "default" as const,
          }));
          const configured = await openConfigureChips(initial, { pageId });
          if (!configured) return;
          const chips = configured.map((c) => ({
            iconSrc: c.iconSrc,
            iconAlt: c.iconAlt,
            label: c.label,
            variant: c.variant,
            href: c.href.trim() || null,
          }));
          const ok = editor
            .chain()
            .focus()
            .insertIconChipGrid({ chips })
            .run();
          if (!ok) {
            notifications.show({
              color: "red",
              message: "Insert failed: schema rejected iconChipGrid.",
            });
          }
        } catch (err: any) {
          notifications.show({
            color: "red",
            message: `Icon chip grid: ${err?.message ?? String(err)}`,
          });
        }
      },
    },
    {
      title: "Inline highlight",
      description: "Pick an icon (B2), then edit the one-line text.",
      searchTerms: ["highlight", "inline", "card", "note", "callout"],
      icon: IconInfoCircle,
      command: async ({ editor, range }: CommandProps) => {
        editor.chain().focus().deleteRange(range).run();
        // @ts-ignore
        const pageId = editor.storage?.pageId as string | undefined;
        if (!pageId) {
          notifications.show({
            color: "red",
            message: "Cannot insert: page is not saved yet (no pageId).",
          });
          return;
        }
        try {
          const [ic] = await pickAndUploadIcons(pageId);
          // Mantine modal — replaces window.prompt so the prompt UI matches
          // the rest of the editor and supports variant + icon swap.
          const next = await openSingleIconText(
            {
              iconSrc: ic?.src ?? "",
              iconAlt: ic?.alt ?? "",
              text: "",
              variant: "default",
            },
            {
              pageId,
              title: "Inline highlight",
              mode: "insert",
            },
          );
          if (!next) return;
          const ok = editor
            .chain()
            .focus()
            .insertInlineHighlight({
              iconSrc: next.iconSrc || null,
              iconAlt: next.iconAlt ?? "",
              text: next.text,
              variant: next.variant,
            })
            .run();
          if (!ok) {
            notifications.show({
              color: "red",
              message: "Insert failed: schema rejected inlineHighlight.",
            });
          }
        } catch (err: any) {
          notifications.show({
            color: "red",
            message: `Inline highlight: ${err?.message ?? String(err)}`,
          });
        }
      },
    },
    {
      title: "Feature list",
      description: "Pick N icons (B2), then fill title/badge/description per row.",
      searchTerms: ["features", "list", "cards", "stages", "tiers"],
      icon: IconList,
      command: async ({ editor, range }: CommandProps) => {
        editor.chain().focus().deleteRange(range).run();
        // @ts-ignore
        const pageId = editor.storage?.pageId as string | undefined;
        if (!pageId) {
          notifications.show({
            color: "red",
            message: "Cannot insert: page is not saved yet (no pageId).",
          });
          return;
        }
        try {
          const icons = await pickAndUploadIcons(pageId, { multiple: true });
          // eslint-disable-next-line no-console
          console.log("[slash:featureList] icons", icons);
          if (icons.length === 0) return;
          const initial = icons.map((ic, i) => ({
            iconSrc: ic.src,
            iconAlt: ic.alt,
            title: `Feature ${i + 1}`,
            badge: "",
            text: "",
            variant: "default" as const,
          }));
          const configured = await openConfigureFeatures(initial, { pageId });
          // eslint-disable-next-line no-console
          console.log("[slash:featureList] configured", configured);
          if (!configured) return;
          // eslint-disable-next-line no-console
          console.log("[slash:featureList] about to insert", { items: configured });
          let ok = false;
          try {
            ok = editor
              .chain()
              .focus()
              .insertFeatureList({ items: configured })
              .run();
            // eslint-disable-next-line no-console
            console.log("[slash:featureList] insertFeatureList returned", ok);
          } catch (insertErr: any) {
            // eslint-disable-next-line no-console
            console.error("[slash:featureList] insertFeatureList THREW", insertErr);
            notifications.show({
              color: "red",
              title: "Feature list insert threw",
              message: insertErr?.message ?? String(insertErr),
              autoClose: 15000,
            });
            return;
          }
          if (!ok) {
            notifications.show({
              color: "red",
              title: "Feature list insert returned false",
              message:
                "ProseMirror rejected the insertion. Open DevTools console — `[slash:featureList]` entries show what was attempted.",
              autoClose: 15000,
            });
          }
        } catch (err: any) {
          // eslint-disable-next-line no-console
          console.error("[slash:featureList] outer catch", err);
          notifications.show({
            color: "red",
            message: `Feature list: ${err?.message ?? String(err)}`,
          });
        }
      },
    },
    {
      title: "Titled frame",
      description: "Bordered section with a centered title at the top.",
      searchTerms: ["frame", "section", "border", "panel", "titled"],
      icon: IconAppWindow,
      command: async ({ editor, range }: CommandProps) => {
        editor.chain().focus().deleteRange(range).run();
        const raw = await openTextPrompt({
          title: "Titled frame",
          label: "Frame title",
          initial: "Section",
          placeholder: "e.g. How it works",
        });
        if (raw === null) return;
        const title = raw.trim() || "Section";
        const ok = editor.chain().focus().insertTitledFrame({ title }).run();
        if (!ok) {
          notifications.show({
            color: "red",
            message: "Insert failed: schema rejected titledFrame.",
          });
        }
      },
    },
    {
      title: "Section divider",
      description: "Full-width heading with ornament strokes on both sides.",
      searchTerms: ["divider", "section", "separator", "rule", "heading"],
      icon: IconMenu4,
      command: async ({ editor, range }: CommandProps) => {
        editor.chain().focus().deleteRange(range).run();
        const raw = await openTextPrompt({
          title: "Section divider",
          label: "Divider title",
          initial: "Section",
          placeholder: "e.g. Hero",
        });
        if (raw === null) return;
        const title = raw.trim() || "Section";
        const ok = editor.chain().focus().insertSectionDivider({ title }).run();
        if (!ok) {
          notifications.show({
            color: "red",
            message: "Insert failed: schema rejected sectionDivider.",
          });
        }
      },
    },
    {
      title: "Footer callout",
      description: "Pick an icon (B2), then edit the one-line summary.",
      searchTerms: ["footer", "callout", "strip", "summary", "warning"],
      icon: IconInfoCircle,
      command: async ({ editor, range }: CommandProps) => {
        editor.chain().focus().deleteRange(range).run();
        // @ts-ignore
        const pageId = editor.storage?.pageId as string | undefined;
        if (!pageId) {
          notifications.show({
            color: "red",
            message: "Cannot insert: page is not saved yet (no pageId).",
          });
          return;
        }
        try {
          const [ic] = await pickAndUploadIcons(pageId);
          const next = await openSingleIconText(
            {
              iconSrc: ic?.src ?? "",
              iconAlt: ic?.alt ?? "",
              text: "",
              variant: "default",
            },
            {
              pageId,
              title: "Footer callout",
              mode: "insert",
            },
          );
          if (!next) return;
          const ok = editor
            .chain()
            .focus()
            .insertFooterCallout({
              iconSrc: next.iconSrc || null,
              iconAlt: next.iconAlt ?? "",
              text: next.text,
              variant: next.variant,
            })
            .run();
          if (!ok) {
            notifications.show({
              color: "red",
              message: "Insert failed: schema rejected footerCallout.",
            });
          }
        } catch (err: any) {
          notifications.show({
            color: "red",
            message: `Footer callout: ${err?.message ?? String(err)}`,
          });
        }
      },
    },
  ],
};

export const getSuggestionItems = ({
  query,
  excludeItems,
}: {
  query: string;
  excludeItems?: Set<string>;
}): SlashMenuGroupedItemsType => {
  const search = query.toLowerCase();
  const filteredGroups: SlashMenuGroupedItemsType = {};

  const fuzzyMatch = (query: string, target: string) => {
    let queryIndex = 0;
    target = target.toLowerCase();
    for (const char of target) {
      if (query[queryIndex] === char) queryIndex++;
      if (queryIndex === query.length) return true;
    }
    return false;
  };

  for (const [group, items] of Object.entries(CommandGroups)) {
    const filteredItems = items.filter((item) => {
      if (excludeItems?.has(item.title)) return false;
      return (
        fuzzyMatch(search, item.title) ||
        item.description.toLowerCase().includes(search) ||
        (item.searchTerms &&
          item.searchTerms.some((term: string) => term.includes(search)))
      );
    });

    if (filteredItems.length) {
      filteredGroups[group] = filteredItems.sort((a, b) => {
        const aTitle = a.title.toLowerCase().includes(search) ? 0 : 1;
        const bTitle = b.title.toLowerCase().includes(search) ? 0 : 1;
        return aTitle - bTitle;
      });
    }
  }

  return filteredGroups;
};

export default getSuggestionItems;
